import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { PersonalAccessToken } from './personal-access-token';
export { PersonalAccessToken };
import { personalAccessTokenSchema } from '@schemas/business-objects/personal-access-token';
import { UserFormGroup } from './user-form-group';
import type {
  MintedPersonalAccessToken,
  PersonalAccessTokenSummary,
} from '../interface-definitions/index';
import type { Id } from '../base-types';

export class PersonalAccessTokenFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    name: SchemaFormControl;
    hash: SchemaFormControl;
    displayPrefix: SchemaFormControl;
    created: SchemaFormControl;
    expiresAt: SchemaFormControl;
    lastUsedAt: SchemaFormControl;
    revoked: SchemaFormControl;
    userId: SchemaFormControl;
    user: UserFormGroup;
  };

  declare value: Partial<PersonalAccessToken>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(personalAccessTokenSchema, PersonalAccessToken, data, options);
  }

  get object(): PersonalAccessToken {
    return new PersonalAccessToken(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'user':
        return new UserFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<PersonalAccessToken[]> {
    return PersonalAccessToken.find(filter);
  }

  static async findOne(filter?: any): Promise<PersonalAccessToken | null> {
    return PersonalAccessToken.findOne(filter);
  }

  static async findById(
    id: number,
    filter?: any,
  ): Promise<PersonalAccessToken> {
    return PersonalAccessToken.findById(id, filter);
  }

  static async create(
    data: Partial<PersonalAccessToken>,
  ): Promise<PersonalAccessToken> {
    return PersonalAccessToken.create(data as any);
  }

  static async updateById(
    id: number,
    updates: Partial<PersonalAccessToken>,
  ): Promise<PersonalAccessToken> {
    return PersonalAccessToken.updateById(id, updates as any);
  }

  static async deleteById(id: number): Promise<boolean> {
    return PersonalAccessToken.deleteById(id);
  }

  static async revoke(tokenId: Id): Promise<void> {
    return PersonalAccessToken.revoke(tokenId);
  }

  static async mint(
    name: string,
    expiresInDays?: number,
  ): Promise<MintedPersonalAccessToken> {
    return PersonalAccessToken.mint(name, expiresInDays);
  }

  static async mine(): Promise<PersonalAccessTokenSummary[]> {
    return PersonalAccessToken.mine();
  }
}

export class PersonalAccessTokenFormArray extends PersistedFormArray {
  readonly entityName = 'PersonalAccessToken' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(personalAccessTokenSchema, PersonalAccessToken, options);
  }

  protected override createItemGroup() {
    return new PersonalAccessTokenFormGroup();
  }

  get array(): PersonalAccessToken[] {
    return this.controls.map(
      (group: PersonalAccessTokenFormGroup) => group.object,
    );
  }
}

export class PersonalAccessTokenPersistedArray extends PersistedArray<PersonalAccessToken> {
  readonly entityName = 'PersonalAccessToken' as const;

  constructor(options?: PersistedArrayOptions) {
    super(PersonalAccessToken, options);
  }
}
